import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  Plus,
  Eye,
  Printer,
  CheckCircle2,
  Package,
  User,
  Calendar,
  AlertCircle,
  TrendingDown,
  TrendingUp,
  FileText,
} from "lucide-react";
import { cn } from "../lib/utils";
import { retrieveData } from "../LocalConnection/LocalConnection.js";
import {
  get_audit_list,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import { handleError } from "../CommonJquery/CommonJquery.js";

export default function AuditListScreen() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [auditData, setAuditData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingAudit, setViewingAudit] = useState(null);

  // Fetch real audit list from backend
  const fetchAudits = useCallback(async () => {
    setLoading(true);
    setError(null);

    const fd = new FormData();
    fd.append("status", "all");
    fd.append("search", "");

    try {
      const response = await server_post_data(get_audit_list, fd);

      if (response.data.error) {
        setError(response.data.message || "Failed to load audits");
        setAuditData([]);
      } else {
        const rawList = response.data.message?.list || [];

        const formatted = rawList.map((audit) => {
          const items = (audit.items || []).map((it) => ({
            ingredient_id: it.item_id,
            item_name: it.item_name || "Unknown Item",
            stock_qty: Number(it.system_qty) || 0,
            physical_qty: Number(it.physical_qty) || 0,
            difference: Number(it.difference_qty) || 0,
            difference_type: it.difference_type || "No Difference",
            reason: it.reason || "",
            unit_name: it.unit_name || "—",
          }));

          // FIXED: Match backend's actual difference_type values ("excess" = gain, "short" = loss)
          const total_loss = items.reduce((sum, i) => {
            const type = (i.difference_type || "").toLowerCase().trim();
            const diff = Number(i.difference) || 0;
            if (
              type === "short" ||
              type.includes("loss") ||
              type === "reduced"
            ) {
              return sum + diff;
            }
            return sum;
          }, 0);

          const total_gain = items.reduce((sum, i) => {
            const type = (i.difference_type || "").toLowerCase().trim();
            const diff = Number(i.difference) || 0;
            if (type === "excess" || type.includes("gain")) {
              return sum + diff;
            }
            return sum;
          }, 0);

          return {
            id: audit.audit_id,
            audit_number: audit.audit_no || `AD-${audit.audit_id}`,
            auditor_name: audit.admin_name || "—",
            audit_date: audit.audit_date || audit.createdAt,
            items_count: audit.total_items || items.length,
            total_loss,
            total_gain,
            status: "Completed",
            created_at:
              audit.createdAt || audit.audit_date || new Date().toISOString(),
            audit_notes: "",
            items,
          };
        });

        setAuditData(formatted);
      }
    } catch (err) {
      console.error("Fetch audits error:", err);
      setError("Network error. Please check your connection.");
      handleError?.("network");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  // Filter and search (client-side)
  const filteredData = useMemo(() => {
    return auditData.filter((audit) => {
      const matchesSearch =
        audit.audit_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        audit.auditor_name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        filterStatus === "all" || audit.status === filterStatus;

      return matchesSearch && matchesStatus;
    });
  }, [auditData, searchQuery, filterStatus]);

  // Format date consistently
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleViewClick = useCallback((audit) => {
    setViewingAudit(audit);
    setIsViewDialogOpen(true);
  }, []);

  const handleCreateAudit = () => {
    navigate("/audit-create");
  };

  const handlePrint = (auditId) => {
    console.log("Print audit:", auditId);
  };

  // Statistics
  const stats = useMemo(() => {
    const completed = auditData.length;
    const pending = 0;
    const total_loss = auditData.reduce(
      (sum, a) => sum + (a.total_loss || 0),
      0,
    );
    const total_gain = auditData.reduce(
      (sum, a) => sum + (a.total_gain || 0),
      0,
    );

    return { completed, pending, total_loss, total_gain };
  }, [auditData]);

  return (
    <MainLayout>
      <Header
        title="Stock Audits"
        subtitle="View all stock audits and reconciliation records"
      />

      <div className="p-6 space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Completed Audits
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-primary">
                    {stats.completed}
                  </p>
                  <span className="text-sm text-green-600">✓</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Pending Audits</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-orange-500">
                    {stats.pending}
                  </p>
                  <span className="text-sm text-orange-600">○</span>
                </div>
              </div>
            </CardContent>
          </Card> */}

          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Total Loss</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-red-600">
                    {stats.total_loss.toFixed(2)}
                  </p>
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Optional: add Total Gain card if you want symmetry */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Total Gain</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-green-600">
                    {stats.total_gain.toFixed(2)}
                  </p>
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search, Filter and Create Button */}
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search audit number or auditor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
            </SelectContent>
          </Select>

          <Button
            onClick={handleCreateAudit}
            className="gap-2 whitespace-nowrap"
            size="lg"
          >
            <Plus className="w-4 h-4" />
            Create Audit
          </Button>
        </div>

        {/* Audits Table */}
        <Card>
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading audits...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center text-destructive">
              <AlertCircle className="h-12 w-12 mx-auto mb-4" />
              <p>{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchAudits}>
                Try Again
              </Button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No audits found</p>
              {(searchQuery || filterStatus !== "all") && (
                <p className="text-sm mt-2">
                  Try adjusting your search or filter
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-muted-foreground">
                      Audit Number
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Auditor
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Items
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Loss
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Gain
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Status
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Date
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((audit) => (
                    <TableRow
                      key={audit.id}
                      className="hover:bg-muted/30 cursor-pointer"
                      onClick={() => handleViewClick(audit)}
                    >
                      <TableCell className="font-medium text-primary">
                        {audit.audit_number}
                      </TableCell>
                      <TableCell>{audit.auditor_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{audit.items_count}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold text-red-600 flex items-center justify-center gap-1">
                          <TrendingDown className="w-4 h-4" />
                          {audit.total_loss.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold text-green-600 flex items-center justify-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          {audit.total_gain.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-green-100 text-green-700 border-green-300">
                          ✓ {audit.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(audit.created_at)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewClick(audit);
                            }}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4 text-muted-foreground hover:text-primary" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrint(audit.id);
                            }}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                            title="Print"
                          >
                            <Printer className="w-4 h-4 text-muted-foreground hover:text-primary" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* View Audit Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Details</DialogTitle>
            <DialogDescription>
              Complete information about this audit
            </DialogDescription>
          </DialogHeader>

          {viewingAudit && (
            <div className="space-y-6 py-4">
              {/* Header Card */}
              <div className="flex items-start justify-between p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                    <Package className="h-8 w-8 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">
                      {viewingAudit.audit_number}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Audit ID: {viewingAudit.id}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <Badge className="bg-green-100 text-green-700 border-green-300 font-medium">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {viewingAudit.status}
                  </Badge>
                </div>
              </div>

              {/* Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Auditor
                    </p>
                  </div>
                  <p className="text-base font-semibold text-foreground ml-10">
                    {viewingAudit.auditor_name}
                  </p>
                </div>

                <div className="p-4 border rounded-lg bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-primary" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Audit Date
                    </p>
                  </div>
                  <p className="text-base font-semibold text-foreground ml-10">
                    {formatDate(viewingAudit.created_at)}
                  </p>
                </div>

                <div className="p-4 border rounded-lg bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Loss
                    </p>
                  </div>
                  <p className="text-base font-semibold text-red-600 ml-10">
                    {viewingAudit.total_loss.toFixed(2)}
                  </p>
                </div>

                <div className="p-4 border rounded-lg bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Gain
                    </p>
                  </div>
                  <p className="text-base font-semibold text-green-600 ml-10">
                    {viewingAudit.total_gain.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Audit Items Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Audit Items ({viewingAudit.items_count})
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-muted-foreground">
                          Sr.No
                        </TableHead>
                        <TableHead className="text-muted-foreground">
                          Item Name
                        </TableHead>
                        <TableHead className="text-center text-muted-foreground">
                          Stock Qty
                        </TableHead>
                        <TableHead className="text-center text-muted-foreground">
                          Physical Qty
                        </TableHead>
                        <TableHead className="text-center text-muted-foreground">
                          Difference
                        </TableHead>
                        <TableHead className="text-muted-foreground">
                          Reason
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingAudit.items?.length > 0 ? (
                        viewingAudit.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">
                              {idx + 1}
                            </TableCell>
                            <TableCell className="font-medium">
                              {item.item_name}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="font-semibold">
                                {item.stock_qty} {item.unit_name}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="font-semibold">
                                {item.physical_qty} {item.unit_name}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                className={cn(
                                  "font-semibold",
                                  item.difference_type?.toLowerCase() ===
                                    "excess" ||
                                    item.difference_type
                                      ?.toLowerCase()
                                      .includes("gain")
                                    ? "bg-green-100 text-green-700 border-green-300"
                                    : item.difference_type?.toLowerCase() ===
                                          "short" ||
                                        item.difference_type
                                          ?.toLowerCase()
                                          .includes("loss")
                                      ? "bg-red-100 text-red-700 border-red-300"
                                      : "bg-gray-100 text-gray-700 border-gray-300",
                                )}
                              >
                                {item.difference_type === "excess"
                                  ? "Gain"
                                  : item.difference_type === "short"
                                    ? "Loss"
                                    : item.difference_type ||
                                      "No Difference"}{" "}
                                ({item.difference.toFixed(2)})
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-xs">
                              <span title={item.reason}>
                                {item.reason || "—"}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center text-muted-foreground py-8"
                          >
                            No items in this audit
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {viewingAudit.audit_notes && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Audit Notes
                  </h4>
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <p className="text-sm text-foreground">
                      {viewingAudit.audit_notes}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
